import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { authClient } from "../lib/authApi";
import { PresetReasonSelect } from "./PresetReasonSelect";
import { SUBMISSION_NOTES } from "../lib/reviewReasonPresets";

// v0.6.12 — record that a return actually reached the authority.
//
// Freezing locks the figures and stamps a reference THIS APP generates. It is
// not evidence that any tax office received anything, and nothing in this app
// submits to one. So a return prepared and frozen but never sent looked
// complete. This panel is where the operator says otherwise, and it captures
// the AUTHORITY's own reference rather than ours.
//
// Only rendered for a frozen calc: an unfrozen one's figures can still change
// under the reference, and the endpoint refuses it for that reason.

const CHANNELS = [
  ["AUTHORITY_PORTAL", "Authority portal"],
  ["TAX_AGENT", "Tax agent"],
  ["SOFTWARE", "Other software"],
  ["POST", "Post"],
];

const todayIso = () => new Date().toISOString().slice(0, 10);

const fieldCls =
  "w-full bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 "
  + "border border-slate-300 dark:border-slate-700 rounded px-2 py-1.5 "
  + "text-xs focus-visible:outline-none focus-visible:ring-2 "
  + "focus-visible:ring-teal-500";

// Labels and helper text: the scale's own size, and no monospace utility —
// that one is reserved for figures (money, IDs, dates) and its use is a
// ratchet sitting at its current count. See tests/designTokens.test.mjs.
const labelCls = "text-xs text-slate-500 dark:text-slate-400";

export const SubmissionRecordPanel = ({ calc }) => {
  const { t } = useTranslation();
  const calcId = calc?.id;
  const frozen = Boolean(calc?.frozen);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    submitted_on: todayIso(), channel: "AUTHORITY_PORTAL",
    authority_reference: "", note: "",
  });
  // v0.6.12 A5-27 — the note is picked; blank stays a real choice (no
  // note), and "Other" opens the box for anything the list cannot say.
  const [notePreset, setNotePreset] = useState("");

  const load = useCallback(async () => {
    if (!calcId || !frozen) { setLoading(false); return; }
    try {
      const { data } = await authClient.get(`/runs/calc/${calcId}/submission`);
      setRecord(data?.submission || null);
    } catch {
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [calcId, frozen]);

  useEffect(() => { load(); }, [load]);

  if (!frozen) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError(null);
    setSaving(true);
    try {
      const { data } = await authClient.post(
        `/runs/calc/${calcId}/submission`,
        {
          submitted_on: form.submitted_on,
          channel: form.channel,
          // Trimmed: a trailing space from a paste would be stored verbatim
          // and then never match the reference the authority holds.
          authority_reference: form.authority_reference.trim() || null,
          note: form.note.trim() || null,
        },
      );
      setRecord(data?.submission || null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail?.message || detail?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <p className="text-xs text-slate-500" data-testid="submission-loading">
        {t("ui.freeze.submission.loading", { defaultValue: "Loading submission…" })}
      </p>
    );
  }

  if (record) {
    const ref = record.authority_reference;
    return (
      <div
        data-testid="submission-recorded"
        className="border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 rounded p-3 space-y-1"
      >
        <p className="text-xs font-semibold text-emerald-900 dark:text-emerald-100">
          {t("ui.freeze.submission.submitted_title", { defaultValue: "Submitted to the authority" })}
        </p>
        <dl className="text-xs text-slate-700 dark:text-slate-300 space-y-0.5">
          <div className="flex gap-2">
            <dt className="text-slate-500 w-28 shrink-0">{t("ui.freeze.submission.date_sent", { defaultValue: "Date sent" })}</dt>
            <dd className="min-w-0 break-words tabular-nums">{record.submitted_on}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-slate-500 w-28 shrink-0">{t("ui.freeze.submission.how", { defaultValue: "How" })}</dt>
            <dd className="min-w-0 break-words">
              {(CHANNELS.find(([v]) => v === record.channel) || [])[1] || record.channel}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-slate-500 w-28 shrink-0">{t("ui.freeze.submission.their_reference", { defaultValue: "Their reference" })}</dt>
            <dd className="min-w-0 break-words" translate="no">
              {ref || <span className="text-slate-400">{t("ui.freeze.submission.none_issued", { defaultValue: "none issued" })}</span>}
            </dd>
          </div>
        </dl>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t("ui.freeze.submission.correct_hint", {
            defaultValue: "To correct this, supersede the calculation and record the new submission against it — the original record is kept.",
          })}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="submission-form"
      className="border border-slate-200 dark:border-slate-700 rounded p-3 space-y-3"
    >
      <div className="space-y-1">
        <h4 className="text-xs font-semibold text-slate-800 dark:text-slate-100">
          {t("ui.freeze.submission.form_title", { defaultValue: "Record the Submission" })}
        </h4>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t("ui.freeze.submission.form_intro", {
            defaultValue: "Freezing locked these figures — it does not mean the return was sent. Record it here once it has actually reached the authority.",
          })}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block space-y-1">
          <span className={labelCls}>{t("ui.freeze.submission.date_sent", { defaultValue: "Date sent" })}</span>
          <input
            type="date" name="submitted_on" required
            value={form.submitted_on} onChange={set("submitted_on")}
            data-testid="submission-date" className={fieldCls}
          />
        </label>
        <label className="block space-y-1">
          <span className={labelCls}>{t("ui.freeze.submission.how_sent", { defaultValue: "How it was sent" })}</span>
          <select
            name="channel" value={form.channel} onChange={set("channel")}
            data-testid="submission-channel" className={fieldCls}
          >
            {CHANNELS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
      </div>

      <label className="block space-y-1">
        <span className={labelCls}>
          {t("ui.freeze.submission.reference_label", { defaultValue: "The authority’s reference (leave blank if none was issued)" })}
        </span>
        <input
          type="text" name="authority_reference" spellCheck={false}
          autoComplete="off" translate="no"
          placeholder={t("ui.filing.submission.reference_placeholder", { defaultValue: "e.g. HMRC-RCPT-99887766…" })}
          value={form.authority_reference} onChange={set("authority_reference")}
          data-testid="submission-reference" className={fieldCls}
        />
      </label>

      <label className="block space-y-1">
        <span className={labelCls}>{t("ui.freeze.submission.note_label", { defaultValue: "Note (optional)" })}</span>
        <PresetReasonSelect
          testid="submission-note-preset"
          presets={SUBMISSION_NOTES}
          preset={notePreset}
          blankLabel="No note"
          onPick={(p, v) => {
            setNotePreset(p);
            setForm((f) => ({ ...f, note: v }));
          }}
          className={fieldCls}
        />
      </label>

      {error && (
        <p
          role="alert" aria-live="polite" data-testid="submission-error"
          className="text-xs text-rose-700 dark:text-rose-300"
        >
          {error}
        </p>
      )}

      <button
        type="submit" disabled={saving} data-testid="submission-save"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs
                   bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white
                   transition-colors focus-visible:outline-none
                   focus-visible:ring-2 focus-visible:ring-teal-400"
        style={{ touchAction: "manipulation" }}
      >
        {saving && (
          <span
            aria-hidden="true"
            className="h-3 w-3 rounded-full border-2 border-white/40 border-t-white motion-safe:animate-spin"
          />
        )}
        {t("ui.freeze.submission.save", { defaultValue: "Record Submission" })}
      </button>
    </form>
  );
};

export default SubmissionRecordPanel;
